// src/page/EventEdit.jsx
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import Select from 'react-select';
import { QRcodeIssuance, revokeObjectUrlSafe } from '../utils/qr';

export default function EventEdit() {
  useAuthGuard();
  const navigate = useNavigate();
  const { eventID } = useParams();

  // ---------- messages ----------
  const MSG = {
    apiError: 'サーバーエラーが発生しました',
    errEventName: 'キャンペーン名は必須です',
    errEventRange: '開催期間は必須です（開始・終了を選択してください）',
    errPublishedFlag: '公開設定を選択してください',
    errPublishedRange: '公開期間は必須です（開始・終了を選択してください）',
    errCampaignPoint: 'キャンペーンポイントは必須です（数値）',
    errOrder: '表示順は整数で入力してください',
    saved: '更新しました',
    deleted: '削除しました',
  };

  // ---------- helpers ----------
  const pad = (n) => String(n).padStart(2, '0');
  const fmtDate = (d) =>
    `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const fmtRange = (a, b) => (a && b ? `${fmtDate(a)} ~ ${fmtDate(b)}` : '');
  const toDateOrNull = (v) => {
    if (!v) return null;
    const d = new Date(v);
    return isNaN(d) ? null : d;
  };
  const isIntegerLike = (v) => v !== '' && /^-?\d+$/.test(String(v).trim());
  const fileURL = (p) => (p ? `${API_BASE_URL.replace(/\/$/, '')}/files/${encodeURIComponent(p)}` : '');
  const isBlobUrl = (u) => { try { return new URL(u).protocol === 'blob:'; } catch { return false; } };
  const isAbsoluteUrl = (u) => /^https?:\/\//i.test(u || '');

  // ---- QR helpers (action) ----
  const safeName = (s) => (s || '')
    .toString()
    .replace(/[^A-Za-z0-9._-]/g, '_')
    .replace(/^_+|_+$/g, '');
  const downloadBlobUrl = (url, filename) => {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'qr.png';
    if (typeof a.download === 'undefined') a.target = '_blank';
    a.click();
  };
  const yyyymmddhhmmss = (d) => {
    const p = (n) => String(n).padStart(2, '0');
    return [
      d.getFullYear(),
      p(d.getMonth() + 1),
      p(d.getDate()),
      p(d.getHours()),
      p(d.getMinutes()),
      p(d.getSeconds())
    ].join('');
  };

  // ---------- states ----------
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState('');
  const [rawEvent, setRawEvent] = useState(null);

  // basics
  const [eventName, setEventName] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [nearest, setNearest] = useState('');
  const [openDisplay, setOpenDisplay] = useState('');
  const [eventOpenDate, setEventOpenDate] = useState(''); // ISO
  const [eventCloseDate, setEventCloseDate] = useState(''); // ISO
  const [eventPeriodDisplay, setEventPeriodDisplay] = useState('');
  const [addressDisplay, setAddressDisplay] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkOpenMode, setLinkOpenMode] = useState('1');
  const [campaignPoint, setCampaignPoint] = useState('');
  const [campaignButtonOn, setCampaignButtonOn] = useState(false);
  const [displayOrder, setDisplayOrder] = useState('');
  const [publishedFlag, setPublishedFlag] = useState(null);
  const [publishedStart, setPublishedStart] = useState(''); // ISO
  const [publishedEnd, setPublishedEnd] = useState('');     // ISO

  // scenes CSV -> array（未使用なら保持だけ）
  const [scenes, setScenes] = useState([]);

  // image
  const [eventImage, setEventImage] = useState('');
  const [imageFile, setImageFile] = useState(null);        // File | null
  const [imagePreview, setImagePreview] = useState('');    // blob/server/absolute URL
  const [serverImagePath, setServerImagePath] = useState(''); // persisted path
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  // ---- action QR (eventID-based) ----
  const [actionQrUrl, setActionQrUrl] = useState('');
  const [actionQrName, setActionQrName] = useState('');
  useEffect(() => {
    revokeObjectUrlSafe(actionQrUrl);
    if (eventID) {
      try {
        const now = new Date();
        const payload = `${eventID}${yyyymmddhhmmss(now)}`;
        const url = QRcodeIssuance(payload, 256);
        setActionQrUrl(url);
        const base = safeName(`${eventID}-${yyyymmddhhmmss(now)}`) || 'action';
        setActionQrName(`${base}.png`);
      } catch {
        setActionQrUrl('');
        setActionQrName('');
      }
    } else {
      setActionQrUrl('');
      setActionQrName('');
    }
    return () => revokeObjectUrlSafe(actionQrUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventID]);

  // ---- spot & ticket (related tickets) ----
  const [spotId, setSpotId] = useState('');
  const [spots, setSpots] = useState([]);
  const [loadingSpots, setLoadingSpots] = useState(false);
  const spotOptions = useMemo(
    () => spots.map(s => ({ value: s.spotId, label: s.spotName })),
    [spots]
  );
  const [ticketId, setTicketId] = useState('');
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const ticketOptions = useMemo(
    () => tickets.map(t => ({ value: t.packageId, label: t.packageName })),
    [tickets]
  );
  const [pairs, setPairs] = useState([]); // [{spotId, spotName, ticketId, ticketName}]
  const [relatedSpotsInit, setRelatedSpotsInit] = useState([]);     // legacy fallback
  const [relatedPackagesInit, setRelatedPackagesInit] = useState([]); // legacy fallback
  const prefilledRef = useRef(false);

  // ---- spot & coupon (related coupons; independent from tickets) ----
  const [spotIdCoupon, setSpotIdCoupon] = useState('');
  const [coupons, setCoupons] = useState([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const couponOptions = useMemo(
    () => coupons.map(c => ({ value: c.packageId, label: c.packageName })),
    [coupons]
  );
  const [couponId, setCouponId] = useState('');
  const [couponPairs, setCouponPairs] = useState([]); // [{ spotId, spotName, couponId, couponName }]
  const [couponSpotsInit, setCouponSpotsInit] = useState([]); // legacy fallback
  const [couponsInit, setCouponsInit] = useState([]); // legacy fallback
  const prefilledCouponRef = useRef(false);

  // validation
  const [errors, setErrors] = useState({});

  // submit/delete states
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const savingOrUploading = submitting || uploading || deleting;

  // flatpickr refs
  const openRangeRef = useRef(null);
  const publishRangeRef = useRef(null);
  // file input ref
  const fileInputRef = useRef(null);
  const setSingleImage = (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) { alert('画像ファイルを選択してください'); return; }
    if (imagePreview && isBlobUrl(imagePreview)) URL.revokeObjectURL(imagePreview);
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };
  const handlePickClick = () => fileInputRef.current?.click();
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setSingleImage(file);
    e.target.value = '';
  };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragOver(false);
    const file = e.dataTransfer.files?.[0]; if (file) setSingleImage(file);
  };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); };
  const clearImage = () => {
    if (imagePreview && isBlobUrl(imagePreview)) URL.revokeObjectURL(imagePreview);
    setImageFile(null); setImagePreview(''); setServerImagePath('');
  };
  useEffect(() => () => { if (imagePreview && isBlobUrl(imagePreview)) URL.revokeObjectURL(imagePreview); }, [imagePreview]);

  const clearPublish = () => {
    setPublishedStart('');
    setPublishedEnd('');
    try { publishRangeRef.current?.clear(); } catch { }
  };

  // flatpickr init
  useEffect(() => {
    if (!window.flatpickr) return;
    const ja = window.flatpickr.l10ns?.ja || {};
    const openInput = document.getElementById('public_range_main');
    if (openInput) {
      openRangeRef.current = window.flatpickr(openInput, {
        mode: 'range',
        enableTime: true,
        time_24hr: true,
        dateFormat: 'Y/m/d H:i',
        locale: { ...ja, rangeSeparator: ' ~ ' },
        onChange: (selectedDates) => {
          if (selectedDates?.length === 2) {
            setEventOpenDate(selectedDates[0].toISOString());
            setEventCloseDate(selectedDates[1].toISOString());
          }
        }
      });
    }
    const pubInput = document.getElementById('public_range_side');
    if (pubInput) {
      publishRangeRef.current = window.flatpickr(pubInput, {
        mode: 'range',
        enableTime: true,
        time_24hr: true,
        dateFormat: 'Y/m/d H:i',
        locale: { ...ja, rangeSeparator: ' ~ ' },
        onChange: (selectedDates) => {
          if (selectedDates?.length === 2) {
            setPublishedStart(selectedDates[0].toISOString());
            setPublishedEnd(selectedDates[1].toISOString());
          }
        }
      });
    }
    return () => {
      try { openRangeRef.current?.destroy(); } catch { }
      try { publishRangeRef.current?.destroy(); } catch { }
    };
  }, []);

  // fetch detail
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setApiError('');
        const token = localStorage.getItem('access_token') || '';

        const res = await fetch(`${API_BASE_URL}/v1/event/${eventID}`, {
          headers: {
            Accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          signal: ac.signal
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const ev = data?.event || {};
        setRawEvent(ev);
        const rel = data?.related || {};

        // ---- tickets (pairs preferred) ----
        const relPairs = Array.isArray(rel.pairs) ? rel.pairs : [];
        if (relPairs.length) {
          const initial = relPairs.map((r) => {
            const s = r.spot || {};
            const p = r.package || {};
            return {
              spotId: s.ID ?? s.id ?? '',
              spotName: s.Name ?? s.name ?? '',
              ticketId: p.ID ?? p.id ?? '',
              ticketName: p.Name ?? p.name ?? (p.ID ?? '')
            };
          }).filter(x => x.spotId || x.ticketId);
          setPairs(initial);
          prefilledRef.current = true;
        } else {
          // legacy arrays
          const relSpots = Array.isArray(rel.spots) ? rel.spots.filter(Boolean) : [];
          const relPkgs = Array.isArray(rel.packages) ? rel.packages.filter(Boolean) : [];
          setRelatedSpotsInit(relSpots);
          setRelatedPackagesInit(relPkgs);
        }

        // ---- coupons (pairs preferred) ----
        const relCouponPairs = Array.isArray(rel.couponPairs) ? rel.couponPairs : [];
        if (relCouponPairs.length) {
          const initialC = relCouponPairs.map((r) => {
            const s = r.spot || {};
            const p = r.package || {};
            return {
              spotId: s.ID ?? s.id ?? '',
              spotName: s.Name ?? s.name ?? '',
              couponId: p.ID ?? p.id ?? '',
              couponName: p.Name ?? p.name ?? (p.ID ?? '')
            };
          }).filter(x => x.spotId || x.couponId);
          setCouponPairs(initialC);
          prefilledCouponRef.current = true;
        } else {
          // legacy arrays
          const cSpots = Array.isArray(rel.couponSpots) ? rel.couponSpots.filter(Boolean) : [];
          const cPkgs = Array.isArray(rel.coupons) ? rel.coupons.filter(Boolean) : [];
          setCouponSpotsInit(cSpots);
          setCouponsInit(cPkgs);
        }

        // basics
        setEventName(ev.eventName ?? '');
        setEventDescription(ev.eventDescription ?? '');
        setNearest(ev.nearest ?? '');
        setOpenDisplay(ev.eventOpenDisplay ?? '');

        const openA = toDateOrNull(ev.eventOpenDate);
        const openB = toDateOrNull(ev.eventCloseDate);
        setEventOpenDate(openA ? openA.toISOString() : '');
        setEventCloseDate(openB ? openB.toISOString() : '');
        if (openRangeRef.current && openA && openB) {
          openRangeRef.current.setDate([fmtDate(openA), fmtDate(openB)], true);
        }

        setEventPeriodDisplay(ev.eventPeriod ?? '');
        setAddressDisplay(ev.eventAddress ?? '');
        setLinkUrl(ev.eventLink ?? '');
        setLinkOpenMode((ev.launcherMode ?? '1').toString() === '2' ? '2' : '1');

        setSpotId(ev.spotId || ev.spotID || ev.spot_id || '');
        setCampaignPoint(ev.campaignPoint != null ? String(ev.campaignPoint) : '');
        setCampaignButtonOn(Boolean(ev.campaignApplicationFlg));
        setDisplayOrder(ev.displayOrder != null ? String(ev.displayOrder) : '');
        setPublishedFlag(typeof ev.publishedFlag === 'boolean' ? ev.publishedFlag : null);

        const pubA = toDateOrNull(ev.publishedStartDate);
        const pubB = toDateOrNull(ev.publishedEndDate);
        setPublishedStart(pubA ? pubA.toISOString() : '');
        setPublishedEnd(pubB ? pubB.toISOString() : '');
        if (publishRangeRef.current && pubA && pubB) {
          publishRangeRef.current.setDate([fmtDate(pubA), fmtDate(pubB)], true);
        }

        const persisted = ev.eventImagePath || ev.eventImage || '';
        setServerImagePath(persisted);
        if (persisted) {
          setImagePreview(isAbsoluteUrl(persisted) ? persisted : fileURL(persisted));
        } else {
          setImagePreview('');
        }
        setEventImage(persisted || '');

      } catch (e) {
        if (e.name !== 'AbortError') setApiError(MSG.apiError);
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [eventID]);

  // Prefill (tickets) only for legacy (no pairs provided)
  useEffect(() => {
    if (prefilledRef.current) return;
    const spotNameMap = new Map(spotOptions.map(o => [o.value, o.label]));
    const norm = (arr) => (Array.isArray(arr) ? arr : [])
      .map(v => {
        if (v && typeof v === 'object') {
          const id =
            v.id ?? v.ID ??
            v.spotId ?? v.spotID ?? v.spot_id ??
            v.packageId ?? v.packageID ?? v.package_id ?? '';
          const name =
            v.name ?? v.Name ??
            v.spotName ?? v.SpotName ??
            v.packageName ?? v.PackageName ?? '';
          return { id, name };
        }
        return { id: String(v || ''), name: '' };
      })
      .filter(x => x.id);
    const S = norm(relatedSpotsInit);
    const P = norm(relatedPackagesInit);
    const initial = [];
    const n = Math.max(S.length, P.length);
    for (let i = 0; i < n; i++) {
      const s = S[i] || { id: '', name: '' };
      const p = P[i] || { id: '', name: '' };
      initial.push({
        spotId: s.id,
        spotName: (s.name && String(s.name)) || (s.id ? (spotNameMap.get(s.id) || s.id) : ''),
        ticketId: p.id,
        ticketName: (p.name && String(p.name)) || p.id
      });
    }
    if (initial.length) {
      const uniq = [];
      const seen = new Set();
      for (const r of initial) {
        const k = `${r.spotId}::${r.ticketId}`;
        if (seen.has(k)) continue;
        seen.add(k);
        uniq.push(r);
      }
      setPairs(uniq);
      prefilledRef.current = true;
    }
  }, [relatedSpotsInit, relatedPackagesInit, spotOptions]);

  // Prefill (coupons) only for legacy (no pairs provided)
  useEffect(() => {
    if (prefilledCouponRef.current) return;
    const spotNameMap = new Map(spotOptions.map(o => [o.value, o.label]));
    const norm = (arr) => (Array.isArray(arr) ? arr : [])
      .map(v => {
        if (v && typeof v === 'object') {
          const id =
            v.id ?? v.ID ??
            v.spotId ?? v.spotID ?? v.spot_id ??
            v.packageId ?? v.packageID ?? v.package_id ?? '';
          const name =
            v.name ?? v.Name ??
            v.spotName ?? v.SpotName ??
            v.packageName ?? v.PackageName ?? '';
          return { id, name };
        }
        return { id: String(v || ''), name: '' };
      })
      .filter(x => x.id);
    const S = norm(couponSpotsInit);
    const P = norm(couponsInit);
    const initial = [];
    const n = Math.max(S.length, P.length);
    for (let i = 0; i < n; i++) {
      const s = S[i] || { id: '', name: '' };
      const p = P[i] || { id: '', name: '' };
      initial.push({
        spotId: s.id,
        spotName: (s.name && String(s.name)) || (s.id ? (spotNameMap.get(s.id) || s.id) : ''),
        couponId: p.id,
        couponName: (p.name && String(p.name)) || p.id
      });
    }
    if (initial.length) {
      const uniq = [];
      const seen = new Set();
      for (const r of initial) {
        const k = `${r.spotId}::${r.couponId}`;
        if (seen.has(k)) continue;
        seen.add(k);
        uniq.push(r);
      }
      setCouponPairs(uniq);
      prefilledCouponRef.current = true;
    }
  }, [couponSpotsInit, couponsInit, spotOptions]);

  // fetch spot list for selector
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoadingSpots(true);
        const token = localStorage.getItem('access_token') || '';
        const res = await fetch(`${API_BASE_URL}/v1/spot?page=1&size=200`, {
          headers: {
            Accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          signal: ac.signal
        });
        const data = await res.json().catch(() => ({}));
        const list = Array.isArray(data.items) ? data.items : (Array.isArray(data) ? data : []);
        const mapped = list.map(s => ({
          spotId: s.spotId ?? s.id ?? s.spot_id ?? '',
          spotName: s.spotName ?? s.name ?? s.spot_name ?? ''
        })).filter(x => x.spotId && x.spotName);
        setSpots(mapped);
      } catch {
        setSpots([]);
      } finally {
        setLoadingSpots(false);
      }
    })();
    return () => ac.abort();
  }, []);

  // fetch ticket packages for selected spot
  useEffect(() => {
    if (!spotId) { setTickets([]); setTicketId(''); return; }
    const ac = new AbortController();
    (async () => {
      try {
        setLoadingTickets(true);
        const token = localStorage.getItem('access_token') || '';
        const url = new URL(`${API_BASE_URL}/v1/ticket`);
        url.searchParams.set('size', '200');
        url.searchParams.set('sort', 'title');
        url.searchParams.set('dir', 'asc');
        url.searchParams.set('spotid', spotId);
        const res = await fetch(url.toString(), {
          headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          signal: ac.signal
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json().catch(() => ({}));
        setTickets(Array.isArray(data.items) ? data.items : []);
      } catch {
        setTickets([]);
      } finally {
        setLoadingTickets(false);
      }
    })();
    return () => ac.abort();
  }, [spotId]);

  // fetch coupon packages for selected spot (independent selector)
  useEffect(() => {
    if (!spotIdCoupon) { setCoupons([]); setCouponId(''); return; }
    const ac = new AbortController();
    (async () => {
      try {
        setLoadingCoupons(true);
        const token = localStorage.getItem('access_token') || '';
        const url = new URL(`${API_BASE_URL}/v1/coupon`);
        url.searchParams.set('size', '200');
        url.searchParams.set('sort', 'title');
        url.searchParams.set('dir', 'asc');
        url.searchParams.set('s', '0,1'); // pre+active に合わせる
        url.searchParams.set('spotid', spotIdCoupon);
        const res = await fetch(url.toString(), {
          headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          signal: ac.signal
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json().catch(() => ({}));
        setCoupons(Array.isArray(data.items) ? data.items : []);
      } catch {
        setCoupons([]);
      } finally {
        setLoadingCoupons(false);
      }
    })();
    return () => ac.abort();
  }, [spotIdCoupon]);

  const handleAddPair = () => {
    if (!spotId || !ticketId) return;
    const spotName = spotOptions.find(o => o.value === spotId)?.label || '';
    const ticketName = ticketOptions.find(o => o.value === ticketId)?.label || '';
    const dup = pairs.some(p => p.spotId === spotId && p.ticketId === ticketId);
    if (dup) return;
    setPairs(prev => [...prev, { spotId, spotName, ticketId, ticketName }]);
    setTicketId('');
  };
  const handleRemovePair = (idx) => setPairs(prev => prev.filter((_, i) => i !== idx));

  const handleAddCouponPair = () => {
    if (!spotIdCoupon || !couponId) return;
    const spotName = spotOptions.find(o => o.value === spotIdCoupon)?.label || '';
    const couponName = couponOptions.find(o => o.value === couponId)?.label || '';
    const dup = couponPairs.some(p => p.spotId === spotIdCoupon && p.couponId === couponId);
    if (dup) return;
    setCouponPairs(prev => [...prev, { spotId: spotIdCoupon, spotName, couponId, couponName }]);
    setCouponId('');
  };
  const handleRemoveCouponPair = (idx) =>
    setCouponPairs(prev => prev.filter((_, i) => i !== idx));

  // ---------- validation ----------
  const invalid = (key) => (errors[key] ? 'is-invalid' : '');
  const feedback = (key) => errors[key] ? <div className="invalid-feedback d-block">{errors[key]}</div> : null;

  const validate = () => {
    const err = {};
    if (!eventName.trim()) err.eventName = MSG.errEventName;
    if (!eventOpenDate || !eventCloseDate) err.eventRange = MSG.errEventRange;
    if (publishedFlag !== true && publishedFlag !== false) err.publishedFlag = MSG.errPublishedFlag;
    if (!publishedStart || !publishedEnd) err.publishedRange = MSG.errPublishedRange;
    if (campaignPoint === '' || Number.isNaN(Number(campaignPoint))) err.campaignPoint = MSG.errCampaignPoint;
    if (displayOrder !== '' && !isIntegerLike(displayOrder)) err.displayOrder = MSG.errOrder;
    setErrors(err);
    return err;
  };

  // ---------- save ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    const keys = Object.keys(err);
    if (keys.length > 0) {
      const first = keys[0];
      const node =
        document.querySelector(`[data-error-key="${first}"]`) ||
        document.querySelector(`[name="${first}"]`) ||
        document.getElementById(first);
      if (node && node.scrollIntoView) {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    const token = localStorage.getItem('access_token') || '';
    const uploadImageIfNeeded = async () => {
      if (!imageFile) return null;
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append('file', imageFile, imageFile.name);
        fd.append('path', `event/${eventID}/`);
        const res = await fetch(`${API_BASE_URL}/v1/file`, {
          method: 'POST',
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: fd
        });
        if (!res.ok) {
          let msg = `画像アップロードに失敗しました (HTTP ${res.status})`;
          try { const j = await res.json(); if (j?.error) msg = j.error; } catch { }
          throw new Error(msg);
        }
        let resultPath = null;
        try { const j = await res.json(); resultPath = j?.path || j?.result || null; }
        catch { resultPath = await res.text(); }
        return resultPath;
      } finally {
        setUploading(false);
      }
    };

    let uploadedImagePath = null;
    if (imageFile) {
      uploadedImagePath = await uploadImageIfNeeded();
      if (!uploadedImagePath) { alert('画像のアップロードに失敗しました。'); return; }
    }

    const uniqueSpotIds = [...new Set(pairs.map(p => p.spotId).filter(Boolean))];
    const uniquePackageIds = [...new Set(pairs.map(p => p.ticketId).filter(Boolean))];
    const uniqueCouponSpotIds = [...new Set(couponPairs.map(p => p.spotId).filter(Boolean))];
    const uniqueCouponIds = [...new Set(couponPairs.map(p => p.couponId).filter(Boolean))];

    const dto = {
      event: {
        eventName,
        eventImage: uploadedImagePath ?? serverImagePath ?? eventImage ?? '',
        eventOpenDate: eventOpenDate || undefined,
        eventCloseDate: eventCloseDate || undefined,
        displayOrder: displayOrder === '' ? undefined : Number(displayOrder),
        publishedFlag: publishedFlag,
        publishedStartDate: publishedStart || undefined,
        publishedEndDate: publishedEnd || undefined,
        eventDescription,
        eventAddress: addressDisplay,
        nearest,
        eventPeriod: eventPeriodDisplay,
        eventLink: linkUrl,
        launcherMode: linkOpenMode,
        eventOpenDisplay: openDisplay,
        campaignPoint: campaignPoint === '' ? undefined : Number(campaignPoint),
        campaignApplicationFlg: !!campaignButtonOn,
      },
      related: {
        // tickets (type=1)
        spots: uniqueSpotIds,
        packages: uniquePackageIds,
        // coupons (type=3)
        couponSpots: uniqueCouponSpotIds,
        coupons: uniqueCouponIds,
      }
    };

    try {
      setSubmitting(true);
      setApiError('');
      const res = await fetch(`${API_BASE_URL}/v1/event/${eventID}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(dto)
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(txt || `HTTP ${res.status}`);
      }
      alert(MSG.saved);
      navigate('/event');
    } catch (e) {
      console.error(e);
      setApiError(MSG.apiError);
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- delete ----------
  const handleDelete = async () => {
    if (!window.confirm('このキャンペーンを削除します。よろしいですか？')) return;
    const token = localStorage.getItem('access_token') || '';
    try {
      setDeleting(true);
      setApiError('');
      const res = await fetch(`${API_BASE_URL}/v1/event/${eventID}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(txt || `HTTP ${res.status}`);
      }
      alert(MSG.deleted);
      navigate('/event');
    } catch (e) {
      console.error(e);
      setApiError(MSG.apiError);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Layout>
      <>
        <nav className="mb-3" aria-label="breadcrumb">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item"><a href="/event">キャンペーン管理</a></li>
            <li className="breadcrumb-item active">編集</li>
          </ol>
        </nav>

        {apiError && <div className="alert alert-danger">{apiError}</div>}
        {loading && <div className="text-body-secondary">読み込み中...</div>}

        <form className="mb-9" onSubmit={handleSubmit}>
          <div className="row g-3 flex-between-end mb-5">
            <div className="col-auto">
              <h2 className="mb-2">キャンペーン編集</h2>
              <h5 className="text-body-terTiary fw-semibold">{/* explanation if any */}</h5>
            </div>
            <div className="col-auto">
              <button
                className="btn btn-phoenix-secondary me-2 mb-2 mb-sm-0"
                type="button"
                onClick={() => window.history.back()}
                disabled={submitting || deleting}
              >
                キャンセル
              </button>
              <button
                className="btn btn-primary me-2 mb-2 mb-sm-0"
                type="submit"
                disabled={submitting || uploading || deleting}
              >
                {uploading ? '画像アップロード中...' : (submitting ? '更新中…' : '更新')}
              </button>
              <button
                className="btn btn-danger mb-2 mb-sm-0"
                type="button"
                onClick={handleDelete}
                disabled={submitting || deleting}
              >
                {deleting ? '削除中…' : '削除'}
              </button>
            </div>
          </div>

          <div className="row g-5">
            <div className="col-12 col-xl-8">
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-4">
                    <h4 className="mb-3">キャンペーンID</h4>
                    <input className="form-control" type="text" name="event_id" disabled defaultValue={eventID} />
                  </div>
                </div>
              </div>

              <div className={`mb-4 ${invalid('eventName')}`} data-error-key="eventName">
                <h4 className="mb-3">キャンペーン名<span className="text-danger">*</span></h4>
                <input
                  className={`form-control ${invalid('eventName')}`}
                  type="text"
                  name="eventName"
                  value={eventName}
                  onChange={(e) => setEventName(e.target.value)}
                />
                {feedback('eventName')}
              </div>

              <div className="mb-4">
                <h4 className="mb-3">キャンペーン概要</h4>
                <textarea
                  className="form-control"
                  name="event_description"
                  rows={4}
                  value={eventDescription}
                  onChange={(e) => setEventDescription(e.target.value)}
                />
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="mb-4">
                    <h4 className="mb-3">最寄り</h4>
                    <textarea
                      className="form-control"
                      name="nearest"
                      rows={4}
                      value={nearest}
                      onChange={(e) => setNearest(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-4">
                    <h4 className="mb-3">開催時間</h4>
                    <textarea
                      className="form-control"
                      name="open_display"
                      rows={4}
                      value={openDisplay}
                      onChange={(e) => setOpenDisplay(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className={`col-md-6`} data-error-key="eventRange">
                  <div className="mb-4">
                    <h4 className="mb-3">開催期間<span className="text-danger">*</span></h4>
                    <div id="period_range_area" className="mt-2">
                      <input
                        className={`form-control ${invalid('eventRange')}`}
                        type="text"
                        name="event_range"
                        id="public_range_main"
                        placeholder="YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm"
                        defaultValue={
                          (eventOpenDate && eventCloseDate)
                            ? fmtRange(new Date(eventOpenDate), new Date(eventCloseDate))
                            : ''
                        }
                        readOnly
                      />
                      {feedback('eventRange')}
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-4">
                    <h4 className="mb-3">開催期間（表示用）</h4>
                    <textarea
                      className="form-control"
                      name="event_period_display"
                      rows={4}
                      value={eventPeriodDisplay}
                      onChange={(e) => setEventPeriodDisplay(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <h4 className="mb-3">関連リンク</h4>
                <div className="input-group">
                  <input
                    type="url"
                    className="form-control"
                    name="link_url"
                    placeholder="https://example.com"
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    disabled={savingOrUploading}
                  />
                  <button
                    type="button"
                    className={`btn ${linkOpenMode === '1' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setLinkOpenMode('1')}
                    disabled={savingOrUploading}
                    aria-pressed={linkOpenMode === '1'}
                  >
                    WebView
                  </button>
                  <button
                    type="button"
                    className={`btn ${linkOpenMode === '2' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setLinkOpenMode('2')}
                    disabled={savingOrUploading}
                    aria-pressed={linkOpenMode === '2'}
                  >
                    ブラウザ
                  </button>
                </div>
              </div>

              {/* ------- Related Tickets (horizontal) ------- */}
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-4">
                    <h4 className="mb-3">関連チケット</h4>
                    <div className="input-group align-items-stretch">
                      <div className="flex-grow-1">
                        <Select
                          options={spotOptions}
                          value={spotOptions.find(o => o.value === spotId) || null}
                          onChange={(opt) => setSpotId(opt?.value || '')}
                          isDisabled={loadingSpots || savingOrUploading}
                          isClearable
                          placeholder={loadingSpots ? '読み込み中…' : 'スポット選択'}
                          classNamePrefix="react-select"
                          className="w-100"
                          menuPortalTarget={document.body}
                          styles={{
                            container: (base) => ({ ...base, width: '100%', flex: 1, minWidth: 0 }),
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          }}
                          filterOption={(opt, input) =>
                            opt.label.toLowerCase().includes(input.toLowerCase())
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <h4 className="mb-3">&nbsp;</h4>
                  <div className="input-group align-items-stretch mt-2">
                    <div className="flex-grow-1">
                      <Select
                        options={ticketOptions}
                        value={ticketOptions.find(o => o.value === ticketId) || null}
                        onChange={(opt) => setTicketId(opt?.value || '')}
                        isDisabled={!spotId || loadingTickets || savingOrUploading}
                        isClearable
                        placeholder={!spotId ? '' : (loadingTickets ? '読み込み中…' : '乗車券選択')}
                        classNamePrefix="react-select"
                        className="w-100"
                        menuPortalTarget={document.body}
                        styles={{
                          container: (base) => ({ ...base, width: '100%', flex: 1, minWidth: 0 }),
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                        filterOption={(opt, input) =>
                          opt.label.toLowerCase().includes(input.toLowerCase())
                        }
                      />
                    </div>
                    <span
                      className="input-group-text"
                      role="button"
                      tabIndex={0}
                      aria-label="追加"
                      title="追加"
                      onClick={handleAddPair}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleAddPair(); }}
                      style={{ cursor: (!spotId || !ticketId) ? 'not-allowed' : 'pointer', userSelect: 'none', opacity: (!spotId || !ticketId) ? 0.6 : 1 }}
                    >
                      ＋
                    </span>
                  </div>
                </div>

                <table className="table fs-9 mb-0 mt-3">
                  <thead>
                    <tr>
                      <th className="align-middle ps-3" style={{ width: 40 }}></th>
                      <th className="align-middle ps-3">スポット名</th>
                      <th className="align-middle ps-3">乗車券名</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pairs.length === 0 ? (
                      <tr>
                        <td className="ps-3 text-body-tertiary" colSpan={3}>追加されたデータはありません</td>
                      </tr>
                    ) : (
                      pairs.map((p, idx) => (
                        <tr key={`${p.spotId}-${p.ticketId}-${idx}`}>
                          <td className="ps-3">
                            <button
                              type="button"
                              className="btn btn-link text-danger p-0"
                              title="削除"
                              aria-label="削除"
                              onClick={() => handleRemovePair(idx)}
                              style={{ fontSize: 18, lineHeight: 1, textDecoration: 'none' }}
                            >
                              ×
                            </button>
                          </td>
                          <td className="ps-3">{p.spotName}</td>
                          <td className="ps-3">{p.ticketName}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* ------- Related Coupons (horizontal) ------- */}
              <div className="row mt-6">
                <div className="col-md-6">
                  <div className="mb-4">
                    <h4 className="mb-3">関連クーポン</h4>
                    <div className="input-group align-items-stretch">
                      <div className="flex-grow-1">
                        <Select
                          options={spotOptions}
                          value={spotOptions.find(o => o.value === spotIdCoupon) || null}
                          onChange={(opt) => setSpotIdCoupon(opt?.value || '')}
                          isDisabled={loadingSpots || savingOrUploading}
                          isClearable
                          placeholder={loadingSpots ? '読み込み中…' : 'スポット選択'}
                          classNamePrefix="react-select"
                          className="w-100"
                          menuPortalTarget={document.body}
                          styles={{
                            container: (base) => ({ ...base, width: '100%', flex: 1, minWidth: 0 }),
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          }}
                          filterOption={(opt, input) =>
                            opt.label.toLowerCase().includes(input.toLowerCase())
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <h4 className="mb-3">&nbsp;</h4>
                  <div className="input-group align-items-stretch mt-2">
                    <div className="flex-grow-1">
                      <Select
                        options={couponOptions}
                        value={couponOptions.find(o => o.value === couponId) || null}
                        onChange={(opt) => setCouponId(opt?.value || '')}
                        isDisabled={!spotIdCoupon || loadingCoupons || savingOrUploading}
                        isClearable
                        placeholder={!spotIdCoupon ? '' : (loadingCoupons ? '読み込み中…' : 'クーポン選択')}
                        classNamePrefix="react-select"
                        className="w-100"
                        menuPortalTarget={document.body}
                        styles={{
                          container: (base) => ({ ...base, width: '100%', flex: 1, minWidth: 0 }),
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                        filterOption={(opt, input) =>
                          opt.label.toLowerCase().includes(input.toLowerCase())
                        }
                      />
                    </div>
                    <span
                      className="input-group-text"
                      role="button"
                      tabIndex={0}
                      aria-label="追加"
                      title="追加"
                      onClick={handleAddCouponPair}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleAddCouponPair(); }}
                      style={{ cursor: (!spotIdCoupon || !couponId) ? 'not-allowed' : 'pointer', userSelect: 'none', opacity: (!spotIdCoupon || !couponId) ? 0.6 : 1 }}
                    >
                      ＋
                    </span>
                  </div>
                </div>

                <table className="table fs-9 mb-0 mt-3">
                  <thead>
                    <tr>
                      <th className="align-middle ps-3" style={{ width: 40 }}></th>
                      <th className="align-middle ps-3">スポット名</th>
                      <th className="align-middle ps-3">クーポン名</th>
                    </tr>
                  </thead>
                  <tbody>
                    {couponPairs.length === 0 ? (
                      <tr>
                        <td className="ps-3 text-body-tertiary" colSpan={3}>追加されたデータはありません</td>
                      </tr>
                    ) : (
                      couponPairs.map((p, idx) => (
                        <tr key={`${p.spotId}-${p.couponId}-${idx}`}>
                          <td className="ps-3">
                            <button
                              type="button"
                              className="btn btn-link text-danger p-0"
                              title="削除"
                              aria-label="削除"
                              onClick={() => handleRemoveCouponPair(idx)}
                              style={{ fontSize: 18, lineHeight: 1, textDecoration: 'none' }}
                            >
                              ×
                            </button>
                          </td>
                          <td className="ps-3">{p.spotName}</td>
                          <td className="ps-3">{p.couponName}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

            </div>

            {/* Right column */}
            <div className="col-12 col-xl-4">
              <div className="row g-2">
                <div className="col-12 col-xl-12">
                  <div className="card mb-3" data-error-key="publishedFlag">
                    <div className="card-body">
                      <div className="row gx-3">

                        <div className="col-12 col-sm-6 col-xl-12">
                          <div className={`mb-4 ${invalid('publishedFlag')}`}>
                            <h5 className="mb-3">公開設定<span className="text-danger">*</span></h5>
                            <div className="d-flex align-items-center gap-3">
                              <label className="mb-0">
                                <input
                                  type="radio"
                                  name="publish_status"
                                  value="0"
                                  checked={publishedFlag === false}
                                  onChange={() => setPublishedFlag(false)}
                                /> 非公開
                              </label>
                              <label className="mb-0">
                                <input
                                  type="radio"
                                  name="publish_status"
                                  value="1"
                                  checked={publishedFlag === true}
                                  onChange={() => setPublishedFlag(true)}
                                /> 公開
                              </label>
                            </div>
                            {feedback('publishedFlag')}
                          </div>
                        </div>

                        <div className="col-12 col-sm-6 col-xl-12" data-error-key="publishedRange">
                          <div className="mb-4">
                            <div className="d-flex flex-wrap mb-2">
                              <h5 className="mb-0 text-body-highlight me-2">公開期間<span className="text-danger">*</span></h5>
                            </div>
                            <div className="input-group">
                              <input
                                className={`form-control ${invalid('publishedRange')}`}
                                type="text"
                                name="public_range"
                                id="public_range_side"
                                placeholder="YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm"
                                value={
                                  (publishedStart && publishedEnd)
                                    ? fmtRange(new Date(publishedStart), new Date(publishedEnd))
                                    : ''
                                }
                                readOnly
                              />
                              <span
                                className="input-group-text"
                                role="button"
                                tabIndex={0}
                                onClick={clearPublish}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') clearPublish(); }}
                                aria-label="クリア"
                                title="クリア"
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                              >×</span>
                            </div>
                            {feedback('publishedRange')}
                          </div>
                        </div>

                        <div className="col-12 col-sm-6 col-xl-12" data-error-key="displayOrder">
                          <div className="mb-4">
                            <div className="d-flex flex-wrap mb-2">
                              <h5 className="mb-0 text-body-highlight me-2">表示順</h5>
                            </div>
                            <input
                              className={`form-control ${invalid('displayOrder')}`}
                              type="number"
                              name="order"
                              value={displayOrder}
                              onChange={(e) => setDisplayOrder(e.target.value)}
                            />
                            {feedback('displayOrder')}
                          </div>
                        </div>

                        <div className="col-12 col-sm-6 col-xl-12">
                          <div className="mb-4">
                            <h5 className="mb-3">キャンペーン応募ボタン表示状態</h5>
                            <div className="d-flex align-items-center gap-3">
                              <label className="mb-0">
                                <input
                                  type="radio"
                                  name="campaign_button"
                                  value="0"
                                  checked={!campaignButtonOn}
                                  onChange={() => setCampaignButtonOn(false)}
                                /> OFF
                              </label>
                              <label className="mb-0">
                                <input
                                  type="radio"
                                  name="campaign_button"
                                  value="1"
                                  checked={campaignButtonOn}
                                  onChange={() => setCampaignButtonOn(true)}
                                /> ON
                              </label>
                            </div>
                          </div>
                        </div>

                        <div className="col-12 col-sm-6 col-xl-12" data-error-key="campaignPoint">
                          <div className="mb-4">
                            <h4 className="mb-3">キャンペーンポイント<span className="text-danger">*</span></h4>
                            <input
                              className={`form-control ${invalid('campaignPoint')}`}
                              type="number"
                              name="campaign_point"
                              value={campaignPoint}
                              onChange={(e) => setCampaignPoint(e.target.value)}
                            />
                            {feedback('campaignPoint')}
                          </div>
                        </div>

                      </div>{/* row */}
                    </div>
                  </div>
                </div>

                <div className="col-12 col-xl-12">
                  <div className="card">
                    <div className="card-body">
                      <h4 className="card-title mb-3">キャンペーン画像</h4>
                      {!imagePreview && (
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={handlePickClick}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handlePickClick(); }}
                          onDrop={handleDrop}
                          onDragOver={handleDragOver}
                          onDragEnter={handleDragOver}
                          onDragLeave={handleDragLeave}
                          className={`border border-dashed rounded-3 p-4 text-center ${isDragOver ? 'bg-body-tertiary' : ''}`}
                          style={{ cursor: 'pointer' }}
                          aria-label="画像をドラッグ&ドロップ、またはクリックして選択"
                        >
                          <p className="mb-2">ここに画像をドラッグ＆ドロップ</p>
                          <p className="mb-2">または</p>
                          <button type="button" className="btn btn-outline-primary" disabled={submitting || uploading || deleting}>ファイルを選択</button>
                          <p className="text-body-tertiary mt-2 mb-0">対応形式: 画像ファイル（1枚まで）</p>
                        </div>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                        disabled={submitting || uploading || deleting}
                      />
                      {imagePreview && (
                        <div className="d-flex align-items-start gap-3">
                          <img
                            src={imagePreview}
                            alt="プレビュー"
                            className="rounded border"
                            style={{ width: 160, height: 160, objectFit: 'cover' }}
                          />
                          <div className="flex-grow-1">
                            <div className="mb-2 small text-body-secondary">
                              {imageFile?.name} {imageFile ? `(${Math.round(imageFile.size / 1024)} KB)` : (serverImagePath ? `(サーバー保存: ${serverImagePath})` : '')}
                            </div>
                            <div className="d-flex gap-2">
                              <button type="button" className="btn btn-outline-secondary" onClick={handlePickClick} disabled={submitting || uploading || deleting}>
                                画像を変更
                              </button>
                              <button type="button" className="btn btn-outline-danger" onClick={clearImage} disabled={submitting || uploading || deleting}>
                                画像を削除
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-12 col-xl-12">
                  <div className="card">
                    <div className="card-body">
                      <h4 className="card-title mb-3">行動ポイント用QRコード</h4>
                      {actionQrUrl ? (
                        <>
                          <img
                            src={actionQrUrl}
                            alt="行動ポイント用QRコード"
                            className="border rounded"
                            style={{ width: 180, height: 180 }}
                          />
                          <div className="mt-3">
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => downloadBlobUrl(actionQrUrl, actionQrName)}
                            >
                              ダウンロード
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="text-body-secondary small">event_id が取得できません</div>
                      )}
                    </div>
                  </div>
                </div>

              </div>{/* /row g-2 */}
            </div>{/* /col-12 col-xl-4 */}
          </div>{/* /row g-5 */}
        </form>
      </>
    </Layout>
  );
}
